import { API_BASE_URL } from '../config';

const baseUrl = API_BASE_URL;

// Utility function to handle common headers
const getHeaders = (accessToken, language = "fr") => ({
  Authorization: `Bearer ${accessToken}`,
  "Accept-Language": language || "en",
  Accept: "application/json",
});

// ============ EDIT CUSTOM SERVICE (PUT) ============
export const updatePlaceService = async (serviceId, serviceData, token, language) => {
  try {
    const response = await fetch(`${baseUrl}placeServices/${serviceId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept-Language': language,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(serviceData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to update service');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating place service:', error);
    throw error;
  }
};

// ============ DELETE CUSTOM SERVICE (DELETE) ============
export const deletePlaceService = async (serviceId, token, language) => {
  try {
    const response = await fetch(`${baseUrl}placeServices/${serviceId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept-Language': language,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete service');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting place service:', error);
    throw error;
  }
};


export const submitStep1 = async (data, language = "fr", accessToken) => {
  try {
    const response = await fetch(`${baseUrl}places/steps/1`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": language,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Error submitting Step 1");
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    throw new Error(
      error.message || "Something went wrong while submitting Step 1."
    );
  }
};

export const submitStep2 = async (data, id, language = "fr", accessToken) => {
  try {
    if (!data || !id || !accessToken) {
      throw new Error(
        "Missing required parameters (data, id, or accessToken)."
      );
    }

    console.log('=== Submitting Step 2 ===');
    console.log('ID:', id);

    // Create FormData
    const formData = new FormData();
    const allServices = [];

    // ============ SERVICE PROCESSING WITH TYPE PRESERVATION ============
    if (Array.isArray(data.services)) {
      // First, validate and log any services with missing types
      const servicesWithMissingType = data.services.filter(s => s && !s.type);
      if (servicesWithMissingType.length > 0) {
        console.warn('⚠️ Services with missing type - will attempt to recover:', 
          servicesWithMissingType.map(s => ({ 
            id: s.id, 
            name: s.name,
            is_custom: s.is_custom,
            place_service: s.place_service?.type
          }))
        );
      }

      // Process each service with type preservation
      data.services.forEach((service, index) => {
        if (!service || !service.id) return;

        // CRITICAL: Preserve the original type from the service object
        // Try multiple sources in order of priority
        let serviceType = service.type;
        
        // If type is missing, try to recover from place_service
        if (!serviceType && service.place_service?.type) {
          serviceType = service.place_service.type;
          console.log(`Recovered type for service ${service.id} from place_service:`, serviceType);
        }
        
        // If still missing, determine based on service properties
        if (!serviceType) {
          if (service.is_custom === 1) {
            serviceType = 'onsite';
          } else if (service.available !== undefined) {
            // Try to infer from other properties
            if (service.location_type === 'included' || service.location_type === 'hour') {
              serviceType = 'reservation';
            } else if (service.indicative_price !== undefined) {
              serviceType = 'onsite';
            } else {
              serviceType = 'free';
            }
          } else {
            serviceType = 'free'; // Default fallback
          }
          console.log(`Inferred type for service ${service.id}:`, serviceType);
        }

        // Ensure price is properly formatted for included services
        let servicePrice = service.price;
        if (serviceType === 'reservation' && service.location_type === 'included') {
          servicePrice = 0;
        } else if (servicePrice !== null && servicePrice !== undefined) {
          servicePrice = Number(servicePrice);
        } else {
          servicePrice = null;
        }

        const cleanService = {
          id: service.id,
          name: service.name || service.place_service?.name || 'Unknown Service',
          type: serviceType, // Use the preserved/recovered type
          is_custom: service.is_custom || 0,
          available: service.available === true ? 1 : 0,
          price: servicePrice,
          indicative_price: service.indicative_price !== null && service.indicative_price !== undefined 
            ? Number(service.indicative_price) 
            : null,
          location_type: service.location_type || null,
          description: service.description || null,
          place_id: service.place_id || null,
          image_url: service.image_url || service.place_service?.image_url || '',
          order: service.order || 0,
          location: service.location || false,
          contains: service.contains || false,
        };

        // Validate the service has required fields
        if (!cleanService.type) {
          console.error('❌ Service still missing type after recovery:', cleanService);
          // Force a default type to prevent submission failure
          cleanService.type = 'free';
        }

        const serviceString = JSON.stringify(cleanService);
        const nextIndex = allServices.length;
        
        formData.append(`services[${nextIndex}]`, serviceString);
        allServices.push(cleanService);
        
        console.log(`Added service ${nextIndex}:`, {
          id: cleanService.id,
          name: cleanService.name,
          type: cleanService.type,
          is_custom: cleanService.is_custom
        });
      });
    }

    // ============ HANDLE CUSTOM SERVICES ============
    if (Array.isArray(data.custom_services)) {
      data.custom_services.forEach((service, index) => {
        if (service && service.name && service.name.trim() !== '' && service.price && service.price !== '') {
          
          const cleanService = {
            name: service.name.trim(),
            price: Number(service.price),
            type: 'onsite',
            is_custom: 1,
            available: true,
          };
          
          if (service.id) {
            cleanService.id = service.id;
          }
          
          const serviceString = JSON.stringify(cleanService);
          const nextIndex = allServices.length;
          
          formData.append(`services[${nextIndex}]`, serviceString);
          allServices.push(cleanService);
          console.log('Added custom service:', cleanService.name, cleanService.id ? '(existing)' : '(new)');
        }
      });
    }

    // ============ HANDLE SEMINARS ============
    if (Array.isArray(data.seminars) && data.seminars.length > 0) {
      data.seminars.forEach((seminar, index) => {
        if (seminar && typeof seminar === 'object') {
          const cleanSeminar = {
            id: seminar.id,
            type: seminar.type || '',
            number: seminar.number || '',
            price: seminar.price !== null && seminar.price !== undefined ? Number(seminar.price) : null,
            offer: seminar.offer || null,
          };
          
          const seminarString = JSON.stringify(cleanSeminar);
          formData.append(`seminars[${index}]`, seminarString);
        }
      });
    }

    // ============ HANDLE ALL OTHER FIELDS ============
    // Simple fields - ALWAYS include disposition types with defaults
    const simpleFields = [
      'id', 'place_id', 'place_style_id', 'area', 'desk',
      'type_1_people', 'type_2_people', 'type_3_people', 'type_4_people',
      'type_5_people', 'type_6_people', 'type_7_people', 'max_capacity',
      'coworking_stations'
    ];

    simpleFields.forEach(field => {
      // Always add the field, default to 0 if not present
      const value = data[field] !== undefined && data[field] !== null && data[field] !== '' 
        ? data[field] 
        : 0;
      formData.append(field, String(value));
    });

    // Handle nested objects (flatten with bracket notation)
    const nestedObjects = ['address', 'placeUsage', 'placeStyle', 'placeType'];
    nestedObjects.forEach(objKey => {
      if (data[objKey] && typeof data[objKey] === 'object') {
        Object.keys(data[objKey]).forEach(prop => {
          const value = data[objKey][prop];
          if (value !== null && value !== undefined) {
            formData.append(`${objKey}[${prop}]`, String(value));
          }
        });
      }
    });

    // Handle arrays that should remain as JSON strings
    const jsonArrays = ['images', 'allServices', 'opening_hours'];
    jsonArrays.forEach(arrayKey => {
      if (data[arrayKey] && Array.isArray(data[arrayKey])) {
        formData.append(arrayKey, JSON.stringify(data[arrayKey]));
      }
    });

    // ============ HANDLE IMAGES ============
    for (let i = 1; i <= 6; i++) {
      const imageKey = `image_${i}`;
      const descriptionKey = `image_${i}_description`;
      
      if (data[imageKey] instanceof File) {
        formData.append(imageKey, data[imageKey], data[imageKey].name);
      } else if (typeof data[imageKey] === 'string' && data[imageKey] && 
                 (data[imageKey].startsWith('http') || data[imageKey].startsWith('/'))) {
        formData.append(imageKey, data[imageKey]);
      }
      
      if (data[descriptionKey] && typeof data[descriptionKey] === 'string') {
        formData.append(descriptionKey, data[descriptionKey]);
      }
    }

    // Log final services summary
    console.log('=== Services Summary ===');
    console.log('Total services:', allServices.length);
    console.log('Service types:', allServices.reduce((acc, s) => {
      acc[s.type] = (acc[s.type] || 0) + 1;
      return acc;
    }, {}));

    // Make API call
    const response = await fetch(`${baseUrl}places/steps/2?id=${id}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": language,
        "Accept": "application/json",
        // Also add X-Requested-With for Laravel/Symfony APIs
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    });

    const responseText = await response.text();
    console.log('Raw response:', responseText);

    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response as JSON');
      responseData = { message: responseText };
    }

    if (!response.ok) {
      console.error("Error in submitStep2:", responseData);
      
      // Create a structured error object that will be caught by the component
      const error = new Error(responseData.message || "Submission failed");
      error.response = {
        status: response.status,
        data: responseData,
        headers: response.headers
      };
      
      // Make sure we preserve the errors array if it exists
      if (responseData.errors) {
        error.response.data.errors = responseData.errors;
      }
      
      throw error;
    }

    return responseData;
  } catch (error) {
    console.error("Error in submitStep2:", error);
    
    // If it's already an error with response data, rethrow it
    if (error.response && error.response.data) {
      throw error;
    }
    
    // Create a structured error for other cases
    const structuredError = new Error(error.message || "Something went wrong while submitting Step 2.");
    structuredError.response = {
      status: error.status || 500,
      data: {
        message: error.message || "Unknown error",
        errors: error.errors || [{ code: "unknown", message: error.message || "Unknown error" }]
      }
    };
    
    throw structuredError;
  }
};

export const submitStep3 = async (data, id, language, accessToken) => {
  try {
    const response = await fetch(`${baseUrl}places/steps/3?id=${id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": language,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Error submitting Step 3");
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    throw new Error(
      error.message || "Something went wrong while submitting Step 3."
    );
  }
};

export const submitStep4 = async (data, id, language, accessToken) => {
  try {
    const response = await fetch(`${baseUrl}places/steps/4?id=${id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": language,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Error submitting Step 4");
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    throw new Error(
      error.message || "Something went wrong while submitting Step 4."
    );
  }
};

export const fetchSpaceDataWithAuth = async (placeId, accessToken, language = 'fr') => {
  try {
    if (!baseUrl) {
      throw new Error("API base URL is not defined in environment variables");
    }

    if (!placeId) {
      throw new Error("Place ID is required");
    }

    const url = `${baseUrl}places/${placeId}?lang=${language}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": language,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch data: ${response.status} ${response.statusText}`
      );
    }

    const responseJson = await response.json();
    return responseJson.data || responseJson;
  } catch (error) {
    console.error("Error fetching place data with authorization:", error);
    throw error;
  }
};

export const getPlaceServices = async (id, accessToken, language = 'fr') => {
  if (!id) {
    throw new Error("Place ID is required");
  }

  const url = `${baseUrl}placeServices?id=${id}&lang=${language}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
        "Accept-Language": language,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error("Error fetching place service:", error);
    throw error;
  }
};

export const getPlaceTypes = async (lang, token) => {
  try {
    const response = await fetch(`${baseUrl}placeTypes?lang=${lang}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": lang,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error("Failed to fetch place types:", error);
    throw error;
  }
};

export const deleteSpace = async (spaceId, token) => {
  try {
    const response = await fetch(`${baseUrl}places/${spaceId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to delete space:", error);
    throw error;
  }
};